import type {
  BatchPayload,
  FlamioConfig,
  SessionEndReason,
  Uploader,
} from './types';
import { getDeviceInfo, getUtmParams } from './identity';
import { isMeaningfulEvent } from './session-lifecycle';

const MAX_RETRY_ATTEMPTS = 3;
const BASE_RETRY_DELAY = 1000;
// Bound memory if uploads keep failing; oldest events drop past this.
const MAX_BUFFER_EVENTS = 10000;
const DEFAULT_MAX_CONCURRENT = 15;
const HARD_MAX_CONCURRENT = 100;

// Ingest wire-protocol headers. The server declares the same names; keep both
// ends in sync (HTTP header matching is case-insensitive).
const KEY_HEADER = 'X-Flamio-Key';
const METADATA_HEADER = 'X-Batch-Metadata';
const MAX_CONCURRENT_HEADER = 'X-Flamio-Max-Concurrent';
const USER_ID_HEADER = 'X-Flamio-User-Id';
const USER_TRAITS_HEADER = 'X-Flamio-User-Traits';
const SHED_HEADER = 'x-flamio-shed';
const SHED_HEADER_VALUE = '1';

// The outcome of a send: the batch landed, the server shed this session
// (concurrency cap), or every attempt failed and the caller should re-queue.
type SendResult = 'delivered' | 'shed' | 'failed';

function clampConcurrency(requested: number | undefined): number {
  if (requested === undefined || !Number.isFinite(requested)) {
    return DEFAULT_MAX_CONCURRENT;
  }
  return Math.min(Math.max(Math.floor(requested), 1), HARD_MAX_CONCURRENT);
}

export function createUploader(
  config: FlamioConfig,
  visitorId: string,
  onShed?: () => void
): Uploader {
  let sessionId = crypto.randomUUID();
  const batchInterval = config.batchInterval ?? 5000;
  const batchMaxSize = config.batchMaxSize ?? 50000;
  const retryDelay = config.retryDelayMs ?? BASE_RETRY_DELAY;
  const maxConcurrent = clampConcurrency(config.maxConcurrentSessions);

  let buffer: unknown[] = [];
  let bufferByteSize = 0;
  // Whether the CURRENT buffer holds a meaningful interaction — sent as
  // `meaningful` in the batch metadata so the server advances session liveness
  // without gunzip+parsing the batch (ingestion.service.ts trusts it and only
  // decodes as a fallback for SDKs older than this field).
  let bufferMeaningful = false;
  let batchIndex = 0;
  let flushTimer: ReturnType<typeof setInterval> | null = null;
  let destroyed = false;
  // While suspended (session ended, awaiting a new one) events are dropped, so
  // nothing leaks into the finalized session.
  let suspended = false;
  // Server shed this session over the concurrency cap: stop recording entirely
  // for this page load — no buffering, no sends, no retries.
  let shed = false;
  // A session sends exactly one end batch: beforeunload and pagehide both fire on
  // a real exit, so the second endSession() is dropped until the next rotateSession.
  let ended = false;
  let userId: string | undefined;
  let userTraits: Record<string, unknown> | undefined;
  let currentVisitId: string | undefined;
  // Serialize sends so concurrent flushes can't share a batchIndex.
  let sending: Promise<void> = Promise.resolve();

  flushTimer = setInterval(() => {
    flushInternal(false).catch(noop);
  }, batchInterval);

  function buildPayload(
    eventCount: number,
    sessionEnd: boolean,
    meaningful: boolean,
    reason?: SessionEndReason
  ): BatchPayload {
    const payload: BatchPayload = {
      sessionId,
      batchIndex,
      visitorId,
      eventCount,
      meaningful,
    };

    if (currentVisitId) payload.visitId = currentVisitId;
    if (sessionEnd) {
      payload.sessionEnd = true;
      if (reason) payload.exitReason = reason;
    }

    if (batchIndex === 0) {
      payload.deviceInfo = getDeviceInfo();
      if (config.version) {
        payload.version = config.version;
      }
      try {
        payload.entryUrl = window.location.href;
        payload.referrer = document.referrer || undefined;
      } catch {
        // noop
      }
      const utm = getUtmParams();
      if (Object.keys(utm).length > 0) {
        payload.utmParams = utm;
      }
    }

    return payload;
  }

  async function compressData(
    data: string
  ): Promise<{ body: BodyInit; compressed: boolean }> {
    try {
      if (typeof CompressionStream === 'function') {
        const encoder = new TextEncoder();
        const stream = new Blob([encoder.encode(data)])
          .stream()
          .pipeThrough(new CompressionStream('gzip'));
        const compressedBlob = await new Response(stream).blob();
        return { body: compressedBlob, compressed: true };
      }
    } catch {
      // noop — fall through to raw
    }
    return { body: data, compressed: false };
  }

  // keepalive only on exit: its ~64KB shared quota silently drops bursts if
  // used on every send. Returns 'failed' on network/5xx (caller re-queues).
  async function sendBatch(
    events: unknown[],
    payload: BatchPayload,
    useKeepalive: boolean
  ): Promise<SendResult> {
    const jsonData = JSON.stringify(events);
    const { body, compressed } = await compressData(jsonData);

    const headers: Record<string, string> = {
      [KEY_HEADER]: config.projectKey,
      [METADATA_HEADER]: JSON.stringify(payload),
      [MAX_CONCURRENT_HEADER]: String(maxConcurrent),
    };

    if (compressed) {
      headers['Content-Type'] = 'application/octet-stream';
      headers['Content-Encoding'] = 'gzip';
    } else {
      headers['Content-Type'] = 'application/json';
    }

    if (userId) {
      headers[USER_ID_HEADER] = userId;
    }

    if (userTraits && Object.keys(userTraits).length > 0) {
      try {
        headers[USER_TRAITS_HEADER] = JSON.stringify(userTraits);
      } catch {
        // noop
      }
    }

    for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        const init: RequestInit = { method: 'POST', headers, body };
        if (useKeepalive) init.keepalive = true;
        const response = await fetch(`${config.endpoint}/v1/ingest`, init);

        if (response.ok) return 'delivered';
        if (
          response.status === 429 &&
          response.headers?.get?.(SHED_HEADER) === SHED_HEADER_VALUE
        ) {
          return 'shed';
        }
        // Other 4xx (incl. a plain rate-limit 429): don't retry or re-queue.
        if (response.status >= 400 && response.status < 500) return 'delivered';
      } catch {
        // network error — retry
      }

      if (attempt < MAX_RETRY_ATTEMPTS - 1) {
        await sleep(retryDelay * Math.pow(2, attempt));
      }
    }
    return 'failed';
  }

  function requeue(events: unknown[]): void {
    buffer = events.concat(buffer);
    if (buffer.length > MAX_BUFFER_EVENTS) {
      buffer = buffer.slice(buffer.length - MAX_BUFFER_EVENTS);
    }
    bufferByteSize = buffer.reduce<number>(
      (sum, event) => sum + estimateEventSize(event),
      0
    );
    bufferMeaningful = buffer.some(isMeaningfulEvent);
  }

  async function doFlush(
    useKeepalive: boolean,
    sessionEnd: boolean,
    reason?: SessionEndReason
  ): Promise<void> {
    if (destroyed || shed) return;
    // Sealed: only the session's own end-batch may go out. A periodic flush here
    // would reopen the finalized session, so it is dropped.
    if (suspended && !sessionEnd) return;
    // An end-signal still sends with an empty buffer so the server finalizes
    // inline (idle on an animating page never goes quiet for the cron to catch).
    if (buffer.length === 0 && !sessionEnd) return;
    // Exactly one end batch per session — drop a duplicate (the paired unload
    // events fire endSession twice on a real exit).
    if (sessionEnd) {
      if (ended) return;
      ended = true;
    }

    const flushSessionId = sessionId;
    const events = buffer;
    const meaningful = bufferMeaningful;
    buffer = [];
    bufferByteSize = 0;
    bufferMeaningful = false;
    const payload = buildPayload(events.length, sessionEnd, meaningful, reason);

    const result = await sendBatch(events, payload, useKeepalive);
    if (result === 'delivered') {
      batchIndex++;
    } else if (result === 'shed') {
      handleShed();
    } else if (!suspended && flushSessionId === sessionId) {
      // Re-queue only into the same, still-active session (same index — server
      // ingest is idempotent). A sealed or rotated-away session drops instead,
      // so nothing reopens it or leaks into the next session.
      requeue(events);
    }
  }

  // The server turned this session away (concurrency cap). Go quiet for the rest
  // of the page load and let the host stop its capture sources.
  function handleShed(): void {
    if (shed) return;
    shed = true;
    buffer = [];
    bufferByteSize = 0;
    bufferMeaningful = false;
    if (flushTimer !== null) {
      clearInterval(flushTimer);
      flushTimer = null;
    }
    onShed?.();
  }

  function flushInternal(
    useKeepalive: boolean,
    sessionEnd = false,
    reason?: SessionEndReason
  ): Promise<void> {
    sending = sending.then(() => doFlush(useKeepalive, sessionEnd, reason));
    return sending;
  }

  function estimateEventSize(event: unknown): number {
    try {
      return JSON.stringify(event).length;
    } catch {
      return 0;
    }
  }

  return {
    addEvents(events: unknown[]): void {
      if (destroyed || suspended || shed) return;
      for (const event of events) {
        bufferByteSize += estimateEventSize(event);
        if (!bufferMeaningful && isMeaningfulEvent(event)) {
          bufferMeaningful = true;
        }
      }
      buffer.push(...events);
      if (bufferByteSize >= batchMaxSize) {
        flushInternal(false).catch(noop);
      }
    },

    async flush(final = false): Promise<void> {
      await flushInternal(final);
    },

    async endSession(
      reason: SessionEndReason,
      useKeepalive = false
    ): Promise<void> {
      await flushInternal(useKeepalive, true, reason);
    },

    setVisitId(id: string): void {
      currentVisitId = id;
    },

    suspend(): void {
      suspended = true;
      // Stop the periodic flush so the sealed session receives no further batches.
      if (flushTimer !== null) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
    },

    // Start a fresh tab-session (new id, batch counter, empty buffer) after the
    // previous one ended at an idle boundary — the old session stays sealed.
    rotateSession(): void {
      // A shed session is terminal for this page load — never reopen the timer.
      if (shed) return;
      sessionId = crypto.randomUUID();
      batchIndex = 0;
      buffer = [];
      bufferByteSize = 0;
      bufferMeaningful = false;
      suspended = false;
      ended = false;
      if (flushTimer === null) {
        flushTimer = setInterval(() => {
          flushInternal(false).catch(noop);
        }, batchInterval);
      }
    },

    setIdentity(id: string, traits?: Record<string, unknown>): void {
      userId = id;
      userTraits = traits;
    },

    async destroy(): Promise<void> {
      if (flushTimer !== null) {
        clearInterval(flushTimer);
        flushTimer = null;
      }
      await flushInternal(true);
      destroyed = true;
    },
  };
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
function noop(): void {}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
